import { debounce, isEqual } from 'lodash'
import { useCallback, useRef, useSyncExternalStore } from 'react'
import { uiActions } from 'store/UI/UIReducer'
import { ModalTypeProps, ModalTypes } from 'store/UI/UITypes'
import { useAppDispatch } from 'store/configureStore'

export const openedModalsStack: Array<{
  modalType: ModalTypes
  modalProps: any
}> = []

const subscribers: Set<() => void> = new Set()

const ModalStack = {
  getModals: () => {
    return openedModalsStack
  },

  subscribe: (callback: () => void) => {
    subscribers.add(callback)

    return () => {
      subscribers.delete(callback)
    }
  },

  notify: () => {
    subscribers.forEach((callback) => callback())
  },

  addModal: (modalType: ModalTypes, modalProps: any) => {
    openedModalsStack.push({ modalType, modalProps })

    ModalStack.notify()
  },

  removeModal: () => {
    const removedModal = openedModalsStack.pop()

    ModalStack.notify()

    return removedModal.modalType
  },

  clearStack: () => {
    openedModalsStack.length = 0

    ModalStack.notify()
  },
}

export const useModalStackStore = () => {
  const modalStack = useSyncExternalStore(
    ModalStack.subscribe,
    ModalStack.getModals,
  )

  return {
    modalStack,
    addModal: ModalStack.addModal,
    removeCurrentModal: ModalStack.removeModal,
    clearStack: ModalStack.clearStack,
  }
}

export const useDialogManager = () => {
  const dispatch = useAppDispatch()

  const currentProps = useRef<unknown | undefined>(undefined)
  const currentModalType = useRef<ModalTypes | undefined>(undefined)

  const modalStackStore = useModalStackStore()

  const debouncedOpenModal = useCallback(
    (modalType: ModalTypes, modalProps?: ModalTypeProps) => {
      return debounce(
        () => {
          currentProps.current = modalProps
          currentModalType.current = modalType
          dispatch(uiActions.openModal({ modalProps, modalType }))
        },
        100,
        { leading: true, trailing: false },
      )
    },
    [dispatch],
  )

  const openDialog = useCallback(
    (
      modalType: ModalTypes,
      modalProps?: ModalTypeProps,
      sameParametersClosesTheModal?: boolean,
    ) => {
      const debouncedFn = debouncedOpenModal(modalType, modalProps)
      if (
        sameParametersClosesTheModal &&
        modalProps &&
        isEqual(currentProps.current, modalProps)
      ) {
        dispatch(uiActions.closeModal())
        currentProps.current = undefined
      } else {
        debouncedFn()
      }

      return () => {
        // console.log('canceling debounced open modal')
        debouncedFn.cancel()
      }
    },
    [dispatch, debouncedOpenModal],
  )

  const closeDialog = useCallback(
    (clearStack?: boolean) => {
      const currentModal = modalStackStore.modalStack.at(-1)
      dispatch(uiActions.closeModal({ modalType: currentModal?.modalType }))

      if (clearStack) {
        modalStackStore.clearStack()
      }
    },
    [dispatch, modalStackStore],
  )

  return {
    openDialog,
    closeDialog,
    // currentModalType: modalStackStore.modalStack.at(-1)?.modalType,
    currentModalType: currentModalType.current,
  }
}
